import { useSelector } from "react-redux";
import { MOBILE_SEARCH } from "common/constants";
import Table from "common/Table/Table";
import { ColumnsType } from "antd/es/table";
import AccountAndExchange from "features/portfolio/Accounts/AccountAndExchange";
import useMedia from "utils/hooks/useMedia";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import {
  LanguageNameSpaces,
  PrecisionConfig,
  ProcessedCoinExchangesData,
} from "@shared/types";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import React, { useMemo } from "react";
import { RootState } from "app/store.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface CoinAccountsTableProps {
  data: ProcessedCoinExchangesData[];
  precisionSelector?: (state: RootState) => PrecisionConfig | {} | undefined;
  searchFilter?: string;
}

const CoinAccountsTable = ({
  data,
  precisionSelector,
  searchFilter = "",
}: CoinAccountsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const displayCurrency = useSelector(selectDisplayCurrency);

  const columns: ColumnsType<ProcessedCoinExchangesData> = [
    {
      title: isMobile ? t("accounts.title") : t("accounts.name"),
      dataIndex: "client",
      key: "client",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "client"),
      render: (account: string, coinData) => (
        <AccountAndExchange account={account} exchange={coinData.exchange} />
      ),
    },
    {
      title: t("accounts.total"),
      dataIndex: ["total", displayCurrency],
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      render: (total: number) => (
        <TextWithPrecisionAndIcon
          precisionSelector={precisionSelector}
          text={total}
          round={true}
        />
      ),
      sorter: (a, b) => sortByNumberProperty(a, b, `total.${displayCurrency}`),
    },
    {
      title: t("accounts.available"),
      dataIndex: ["available", displayCurrency],
      showSorterTooltip: false,
      render: (available: number) => {
        return (
          <TextWithPrecisionAndIcon
            precisionSelector={precisionSelector}
            text={available}
            round={true}
          />
        );
      },
      sorter: (a, b) =>
        sortByNumberProperty(a, b, `available.${displayCurrency}`),
    },
  ];

  const filteredData = useMemo(
    () =>
      data.filter((coinData) =>
        coinData.client.toLowerCase().includes(searchFilter.toLowerCase())
      ),
    [data, searchFilter]
  );

  return (
    <Table<ProcessedCoinExchangesData>
      columns={columns}
      dataSource={filteredData}
      rowClassName="table-half-padding"
    />
  );
};

export default CoinAccountsTable;
